import React, { useEffect } from 'react';
import styled from 'styled-components';

interface LightboxProps {
  isOpen: boolean;
  imageSrc: string;
  imageAlt: string;
  onClose: () => void;
}

const LightboxOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LightboxContent = styled.div<{ isOpen: boolean }>`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.3s ease;
`;

const LightboxImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  max-width: 100%;
  max-height: 90vh;

  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -3rem;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #1a365d;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: -2.5rem;
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
`;

const ImageTitle = styled.div`
  position: absolute;
  bottom: -3rem;
  left: 0;
  right: 0;
  color: white;
  text-align: center;
  font-size: 1rem;
  font-weight: 300;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    bottom: -2.5rem;
    font-size: 0.9rem;
  }
`;

const Lightbox: React.FC<LightboxProps> = ({ isOpen, imageSrc, imageAlt, onClose }) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <LightboxOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <LightboxContent isOpen={isOpen}>
        <CloseButton onClick={onClose} aria-label="Close">
          ✕
        </CloseButton>
        <LightboxImage src={imageSrc} alt={imageAlt} />
        <ImageTitle>{imageAlt}</ImageTitle>
      </LightboxContent>
    </LightboxOverlay>
  );
};

export default Lightbox; 